import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PostCard } from "@/pages/Community";
import { toast } from "sonner";
import { useLocation } from "@/context/LocationContext";
import CustomerLayout from "@/components/CustomerLayout";
import ProductCard from "@/components/ProductCard";
import { SkeletonCard, EmptyState } from "@/components/States";
import { Star, Clock, MapPin, ShoppingBag, Bike, Heart } from "lucide-react";
import { useSeo, storeJsonLd } from "@/lib/seo";

export default function StoreDetail() {
  const { id } = useParams();
  const { location } = useLocation();
  const [cat, setCat] = useState("");

  const { data: store } = useQuery({ queryKey: ["store", id], queryFn: () => api.get(`/stores/${id}`, { params: { lat: location.lat, lng: location.lng } }).then((r) => r.data) });
  const { data: products, isLoading } = useQuery({ queryKey: ["store-prod", id], queryFn: () => api.get("/products", { params: { store_id: id } }).then((r) => r.data) });

  const categories = [...new Set((products || []).map((p) => p.category))];
  const shown = cat ? products.filter((p) => p.category === cat) : products;

  useSeo(store ? {
    title: `${store.name} — ${store.area || store.city || "Local store"}`,
    description: store.description || `Order from ${store.name} on Grofkart. Fresh groceries delivered fast.`,
    image: store.cover || store.logo, path: `/store/${store.slug || store.id}`,
    jsonLd: storeJsonLd(store), jsonLdId: "gf-store",
  } : { title: "Store" });

  return (
    <CustomerLayout>
      {store && (
        <div className="mb-6 overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-[0_2px_16px_rgba(0,0,0,0.05)]">
          <div className="relative h-40 sm:h-56">
            <img src={store.cover} alt="" className="h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/25 to-transparent" />
            {store.is_featured && (
              <span className="absolute left-4 top-4 rounded-full bg-amber-surge px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-white">Featured</span>
            )}
            <span className={`absolute right-4 top-4 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-bold text-white ${store.is_accepting_orders ? "bg-brand" : "bg-red-500"}`}>
              <span className="h-1.5 w-1.5 rounded-full bg-white" />{store.is_accepting_orders ? "Open now" : "Closed"}
            </span>
            <div className="absolute bottom-4 left-4 right-4 flex items-end gap-3">
              <img src={store.logo} alt="" className="h-16 w-16 shrink-0 rounded-2xl border-2 border-white object-cover shadow-lg" />
              <div className="min-w-0 flex-1 pb-1 text-white">
                <h1 className="truncate font-head text-2xl font-black tracking-tight">{store.name}</h1>
                {(store.nickname || store.serving_since) && <p className="truncate text-xs font-semibold text-white/85" data-testid="store-identity-line">{store.nickname && `“${store.nickname}”`}{store.nickname && store.serving_since && " · "}{store.serving_since && `Serving since ${store.serving_since}`}</p>}
                <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs font-bold">
                  <span data-testid="store-rating-badge" className="inline-flex items-center gap-1 rounded-md bg-brand px-1.5 py-0.5"><Star className="h-3 w-3 fill-white" />{store.rating} <span className="font-medium text-white/80">({store.reviews_count})</span></span>
                  <span data-testid="store-eta-badge" className="inline-flex items-center gap-1 rounded-md bg-amber-surge px-1.5 py-0.5"><Clock className="h-3 w-3" />{store.delivery_time_min}-{store.delivery_time_max} min</span>
                  {store.distance_km != null && store.distance_km <= 50 && <span data-testid="store-distance-badge" className="inline-flex items-center gap-1 rounded-md bg-white/20 px-1.5 py-0.5 backdrop-blur"><MapPin className="h-3 w-3" />{store.distance_km} km</span>}
                </div>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3 text-xs text-slate-600">
            {(location?.city || location?.address) && (
              <span data-testid="store-delivering-to" className="inline-flex items-center gap-1.5 font-semibold text-slate-700"><MapPin className="h-4 w-4 text-brand" /> Delivering to {location.city || location.address}</span>
            )}
            <span className="h-3 w-px bg-slate-200" />
            <span className="inline-flex items-center gap-1.5"><Bike className="h-4 w-4 text-brand" /> Delivery ₹{store.delivery_fee}</span>
            <span className="inline-flex items-center gap-1.5"><ShoppingBag className="h-4 w-4 text-brand" /> Min order ₹{store.min_order}</span>
          </div>
        </div>
      )}

      {categories.length > 0 && (
        <div className="mb-4 flex items-center gap-2 overflow-x-auto no-scrollbar">
          <button onClick={() => setCat("")} className={`shrink-0 rounded-full px-4 py-1.5 text-sm font-semibold ${!cat ? "bg-brand text-white" : "border border-slate-200 bg-white text-slate-600"}`}>All</button>
          {categories.map((c) => (
            <button key={c} onClick={() => setCat(c)} className={`shrink-0 rounded-full px-4 py-1.5 text-sm font-semibold capitalize ${cat === c ? "bg-brand text-white" : "border border-slate-200 bg-white text-slate-600"}`}>{c.replace(/-/g, " ")}</button>
          ))}
        </div>
      )}

      {isLoading ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
          {Array.from({ length: 10 }).map((_, i) => <SkeletonCard key={i} />)}
        </div>
      ) : shown?.length ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
          {shown.map((p) => <ProductCard key={p.id} product={p} store={store} />)}
        </div>
      ) : (
        <EmptyState icon={ShoppingBag} title="No products available" subtitle="This store hasn't added products yet." />
      )}
      {store && <StoreCommunity store={store} />}
    </CustomerLayout>
  );
}

function StoreCommunity({ store }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { data: feed } = useQuery({ queryKey: ["store-feed", store.id], queryFn: () => api.get("/community/feed", { params: { store_id: store.id } }).then((r) => r.data) });
  const { data: stats } = useQuery({ queryKey: ["store-stats", store.id], queryFn: () => api.get(`/community/stats/${store.id}`).then((r) => r.data) });
  const { data: fol } = useQuery({ queryKey: ["following"], queryFn: () => api.get("/community/following").then((r) => r.data), enabled: !!user });
  const following = fol?.store_ids?.includes(store.id);
  const toggle = async () => { if (!user) return nav("/login"); try { const { data } = await api.post(`/community/follow/${store.id}`); toast.success(data.following ? `You're now a regular at ${store.name} ❤️` : "Removed from your regulars"); qc.invalidateQueries({ queryKey: ["following"] }); qc.invalidateQueries({ queryKey: ["store-stats", store.id] }); } catch (e) { toast.error(apiError(e)); } };
  return (
    <section className="mt-10" data-testid="store-community">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><h2 className="font-head text-xl font-extrabold text-slate-900">{store.nickname || store.name}'s Community</h2><p className="text-xs text-slate-500">{store.tagline || "Serving your neighbourhood"} · {stats?.followers ?? 0} regulars · {stats?.posts ?? 0} posts</p></div>
        <button onClick={toggle} data-testid="become-regular-btn" className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-bold transition-transform active:scale-95 ${following ? "bg-red-50 text-red-500" : "bg-brand text-white"}`}><Heart className={`h-4 w-4 ${following ? "fill-red-500" : ""}`} />{following ? "You're a Regular" : "Become a Regular"}</button>
      </div>
      {(store.story || store.owner_photo || store.serving_since) && <div className="mt-4 flex gap-4 rounded-2xl bg-amber-soft/60 p-4" data-testid="store-our-story">
        {store.owner_photo && <img src={store.owner_photo} alt="" className="h-16 w-16 shrink-0 rounded-full object-cover ring-2 ring-white" data-testid="store-owner-photo" />}
        <div className="min-w-0"><p className="text-xs font-bold uppercase text-amber-warm">Our Story</p><p className="text-xs font-semibold text-slate-600">{store.owner_name && `${store.owner_name} · `}{store.serving_since && `Serving since ${store.serving_since}`}</p>{store.story && <p className="mt-1 whitespace-pre-line text-sm text-slate-700">{store.story}</p>}</div>
      </div>}
      <div className="mt-4 space-y-3">{(feed?.items || []).map((p) => <PostCard key={p.id} p={p} />)}{feed && !feed.items.length && <p className="rounded-2xl border border-dashed border-slate-200 p-6 text-center text-sm text-slate-400">No community posts from this shop yet.</p>}</div>
    </section>
  );
}
